import React, { useState } from 'react';
import { openIngestionEntryFileForView, API_BASE_URL } from '../utils/api';
import { formatEntryFieldDisplay } from '../utils/entryFieldValues';

/**
 * RawEntryFields: shared renderer for displaying an entry's raw fields.
 * - Excludes common system fields.
 * - Truncates long values with a tooltip.
 * - Provides simple className hooks to fit different pages' layouts.
 *
 * Props:
 * - entry: object (required) — key/value pairs to render
 * - fieldsClass?: string — wrapper class for all fields (default 'data-item-fields')
 * - fieldClass?: string — class per field row (default 'data-field')
 * - maxLength?: number — truncate length (default 100)
 * - systemFields?: string[] — additional fields to exclude
 * - fileDownloadContext?: { suffix: string, groupSlug: string } — enables “View file” for blobs in Supabase (needs entry.id)
 */
function RawEntryFields({
  entry,
  fieldsClass = 'data-item-fields',
  fieldClass = 'data-field',
  maxLength = 100,
  systemFields = [],
  // Optional explicit list of keys to render; if provided, we will render these
  // keys in order and show blanks for missing values. Useful to show empty
  // columns based on group-unified keys.
  visibleKeys,
  // Render a dedicated Outputs section when an `output` object exists on the entry
  showOutputs = true,
  outputsTitle = 'Outputs',
  outputsSectionClass = 'outputs-section',
  // Default outputs containers/rows to reuse the same classes as inputs unless explicitly overridden
  outputsFieldsClass,
  outputFieldClass,
  // Optional explicit list of output keys to control order and visibility
  outputVisibleKeys,
  fileDownloadContext,
}) {
  const [openingFileKey, setOpeningFileKey] = useState(null);
  const defaultSystem = ['id', 'created_at', 'updated_at', 'schema_version', 'reviewed', 'approved'];
  // Prefer rendering fields from the canonical raw payload when available
  const base = entry && typeof entry.raw === 'object' && entry.raw !== null && !Array.isArray(entry.raw)
    ? entry.raw
    : entry;
  const yumlViewUrl = base?._yuml_view_url || entry?.raw?._yuml_view_url || null;
  const yumlHref = yumlViewUrl
    ? (String(yumlViewUrl).startsWith('http') ? yumlViewUrl : `${API_BASE_URL.replace(/\/casc\/v1\/api\/?$/, '')}${yumlViewUrl}`)
    : null;

  // Exclude `output` and common system fields only when rendering from a top-level entry object
  const excluded = new Set([...(systemFields || []), ...defaultSystem, 'output', 'raw', 'group_info']);

  // Hide any keys that start with an underscore across preview screens,
  // EXCEPT special file link fields that start with `_file_`
  const isVisibleKey = (k) => {
    if (typeof k !== 'string') return false;
    if (k.startsWith('_file_')) return true; // allow file link fields
    return !k.startsWith('_');
  };

  const getFieldNames = (obj, isRaw) => {
    const keys = Object.keys(obj || {});
    // Always hide underscore-prefixed keys, even from raw
    if (isRaw) return keys.filter(isVisibleKey);
    return keys.filter((k) => isVisibleKey(k) && !excluded.has(k));
  };

  const formatValue = (value) => {
    const str = formatEntryFieldDisplay(value);
    if (str === null) {
      return <span className="empty-value">(empty)</span>;
    }
    if (str.length <= maxLength) {
      return str.includes('\n') ? <pre className="field-value-json">{str}</pre> : str;
    }
    return (
      <span className="field-value-truncated" title={str}>
        {str.substring(0, maxLength)}...
      </span>
    );
  };

  const fileKeysOnEntry = Object.keys(base || {}).filter(
    (k) => k.startsWith('_file_') && isVisibleKey(k)
  );
  const columnKeys = Array.isArray(visibleKeys) && visibleKeys.length > 0
    ? visibleKeys.filter(isVisibleKey)
    : getFieldNames(base, base === entry.raw);
  const fields = Array.isArray(visibleKeys) && visibleKeys.length > 0
    ? [...columnKeys, ...fileKeysOnEntry.filter((k) => !columnKeys.includes(k))]
    : columnKeys;
  const output = entry && typeof entry.output === 'object' && entry.output !== null && !Array.isArray(entry.output)
    ? entry.output
    : null;
  const outputKeys = output
    ? (Array.isArray(outputVisibleKeys) && outputVisibleKeys.length > 0
        ? outputVisibleKeys.filter(isVisibleKey)
        : Object.keys(output).filter(isVisibleKey))
    : [];
  // Ensure outputs use the same styling as the main fields by default
  const effectiveOutputsFieldsClass = outputsFieldsClass || fieldsClass;
  const effectiveOutputFieldClass = outputFieldClass || fieldClass;

  return (
    <div>
      {/* Inputs / base fields */}
      <div className={fieldsClass}>
        {fields.map((name) => {
          const isFileKey = typeof name === 'string' && name.startsWith('_file_');
          const value = base[name];
          if (isFileKey && value && typeof value === 'object' && !Array.isArray(value)) {
            const fileId = name.slice('_file_'.length) || '(unknown)';
            const fileName = typeof value.name === 'string' ? value.name : '';
            const pathStr = typeof value.path === 'string' ? value.path : '';
            const storedInSupabase = Boolean(value.bucket && pathStr && !pathStr.startsWith('gdrive://'));
            const gdrivePlaceholderOnly = pathStr.startsWith('gdrive://') && !storedInSupabase;
            const entryId = entry && (entry.id != null ? entry.id : null);
            const canOpenFile =
              storedInSupabase &&
              fileDownloadContext &&
              fileDownloadContext.suffix &&
              fileDownloadContext.groupSlug &&
              entryId != null;
            const fieldKeyForApi = fileId && fileId !== '(unknown)' ? fileId : 'file';

            return (
              <div key={name} className={fieldClass}>
                <div className="field-name">[File] {fileId}:</div>
                <div className="field-value">
                  {storedInSupabase ? (
                    <span>
                      <strong title={pathStr}>{fileName || '(file)'}</strong>
                      <span className="file-storage-note" style={{ marginLeft: 8, color: 'var(--muted, #666)', fontSize: '0.9em' }}>
                        (stored in Zagger / Supabase Storage)
                      </span>
                      {canOpenFile ? (
                        <>
                          {' '}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            style={{ marginLeft: 8, verticalAlign: 'middle' }}
                            disabled={openingFileKey === name}
                            onClick={async () => {
                              setOpeningFileKey(name);
                              try {
                                await openIngestionEntryFileForView({
                                  suffix: fileDownloadContext.suffix,
                                  groupSlug: fileDownloadContext.groupSlug,
                                  entryId,
                                  field: fieldKeyForApi,
                                });
                              } catch (e) {
                                const msg = e && e.message ? e.message : String(e);
                                window.alert(msg);
                              } finally {
                                setOpeningFileKey(null);
                              }
                            }}
                          >
                            {openingFileKey === name ? 'Opening…' : 'View file'}
                          </button>
                        </>
                      ) : null}
                    </span>
                  ) : gdrivePlaceholderOnly ? (
                    <span>
                      <strong>{fileName || '(file)'}</strong>
                      <span className="file-storage-note" style={{ marginLeft: 8, color: 'var(--muted, #666)', fontSize: '0.9em' }}>
                        (ingestion incomplete — not in storage yet; re-run ingest or check job errors)
                      </span>
                    </span>
                  ) : fileName ? (
                    <span title={pathStr || undefined}>{fileName}</span>
                  ) : (
                    (typeof value === 'object' && value !== null)
                      ? formatValue(JSON.stringify(value))
                      : formatValue(value)
                  )}
                </div>
              </div>
            );
          }

          // Default rendering for non-file keys
          return (
            <div key={name} className={fieldClass}>
              <div className="field-name">{name}:</div>
              <div className="field-value">{typeof value === 'object' && value !== null
                ? formatValue(JSON.stringify(value))
                : formatValue(value)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Outputs section (separate block) */}
      {showOutputs && output && outputKeys.length > 0 && (
        <div className={outputsSectionClass}>
          <h4 style={{ marginTop: 12, marginBottom: 8 }}>{outputsTitle}</h4>
          <div className={effectiveOutputsFieldsClass}>
            {outputKeys.map((key) => (
              <div key={key} className={effectiveOutputFieldClass}>
                <div className="field-name">{key}:</div>
                <div className="field-value">
                  {typeof output[key] === 'object' && output[key] !== null
                    ? formatValue(JSON.stringify(output[key]))
                    : formatValue(output[key])}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {yumlHref && (
        <div className="yuml-entry-link" style={{ marginTop: 8 }}>
          <a href={yumlHref} target="_blank" rel="noopener noreferrer">View YUML formatted output</a>
        </div>
      )}
    </div>
  );
}

export default RawEntryFields;
